/**
 * A category's mark: its emoji on a disc of its colour (DESIGN_SYSTEM §2.3).
 *
 * A disc is a mark, not a container — it holds one glyph, never a group (D-1).
 * The colour is a fill, never text, so it carries no contrast duty; the emoji
 * reads on any of the swatches. The icon always goes through `categoryIcon()`,
 * which never leaves the disc blank (DATA_MODEL §2).
 *
 * Decorative by default — the category's name sits beside it. Pass `label`
 * when the glyph stands alone.
 */

import { categoryIcon } from "@/domain/category_icon";
import { cx } from "@/features/common/primitives";

export function CategoryGlyph({
  category,
  size = "md",
  label,
}: {
  category: { name: string; color: string; icon?: string | null };
  /** `md` is a list row's; `lg` a grid cell's or the picker's preview. */
  size?: "sm" | "md" | "lg";
  label?: string;
}): React.JSX.Element {
  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cx(
        "inline-flex shrink-0 items-center justify-center rounded-full leading-none select-none",
        size === "sm" && "size-phi-1 text-phi-n2",
        size === "md" && "size-phi-3 text-phi-0",
        size === "lg" && "size-phi-4 text-phi-1",
      )}
      // The category's own colour, translucent enough that the emoji's colours
      // stay legible on it in both themes.
      style={{ backgroundColor: `color-mix(in srgb, ${category.color} 32%, transparent)` }}
    >
      {categoryIcon(category)}
    </span>
  );
}
